import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scissors, Clock, Star, Users, Phone, MapPin, ChevronRight, Menu, X, Shield, Award, Zap, ShoppingBag, Camera, ArrowRight, Instagram, Facebook, Sun, Moon, Check, Crown, Infinity, CalendarCheck, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ProductDetailModal from "@/components/ProductDetailModal";
import { useTheme } from "@/contexts/ThemeContext";
import api from "@/lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";
const IMG_HERO = "https://customer-assets.emergentagent.com/job_barbershop-crm-2/artifacts/wvsxqd58_upscalemedia-transformed.jpeg";
const IMG_CUTTING = "https://customer-assets.emergentagent.com/job_barbershop-crm-2/artifacts/p53enr0q_Capster%20Berpengalaman.jpeg";
const IMG_INTERIOR = "https://customer-assets.emergentagent.com/job_barbershop-crm-2/artifacts/jsa75ppr_suasana%20premium.webp";
const IMG_TOOLS = "https://customer-assets.emergentagent.com/job_barbershop-crm-2/artifacts/zg8e6avj_Alat%20%26%20Produk%20Terbaik.jpeg";
const IMG_SALON = "https://customer-assets.emergentagent.com/job_barbershop-crm-2/artifacts/j71222xj_DSC05281-2048x1366.jpg";
const IMG_STYLE1 = "https://images.unsplash.com/photo-1618049049816-43a00d5b0c3d?w=600&q=80";
const IMG_STYLE2 = "https://images.unsplash.com/photo-1633601851802-ad8eb2bcf1f4?w=600&q=80";
const IMG_STYLE3 = "https://images.unsplash.com/photo-1611420745411-ed8fee30b8fb?w=600&q=80";

const categoryIcons = { potong: Scissors, warna: Star, treatment: Star, styling: Scissors };
const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);

export default function LandingPage() {
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [products, setProducts] = useState([]);
  const [capsters, setCapsters] = useState([]);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productDetail, setProductDetail] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { src, name, description }
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  // Theme helpers for public pages
  const bg1 = isDark ? "bg-[#050505]" : "bg-[#FAFAFA]";
  const bg2 = isDark ? "bg-[#0A0A0A]" : "bg-white";
  const bgCard = isDark ? "bg-[#0F0F0F] border-[#1A1A1A]" : "bg-white border-gray-200 shadow-sm";
  const bgCard2 = isDark ? "bg-[#0F0F0F] border-[#262626]" : "bg-white border-gray-200 shadow-sm";
  const bgInner = isDark ? "bg-[#0A0A0A] border-[#1A1A1A]" : "bg-gray-50 border-gray-200";
  const textMain = isDark ? "text-white" : "text-[#1A1A1A]";
  const textSub = isDark ? "text-gray-400" : "text-gray-600";
  const textMuted = isDark ? "text-gray-500" : "text-gray-500";
  const borderC = isDark ? "border-[#1A1A1A]" : "border-gray-200";
  const borderC2 = isDark ? "border-[#262626]" : "border-gray-200";

  useEffect(() => {
    api.get("/services").then(r => setServices(r.data)).catch(() => {});
    api.get("/gallery").then(r => setGallery(r.data)).catch(() => {});
    api.get("/products").then(r => setProducts((r.data || []).filter(p => p.is_active !== false))).catch(() => {});
    api.get("/capsters/public").then(r => setCapsters(r.data)).catch(() => {});
    api.get("/reviews/google").then(r => setGoogleReviews(r.data)).catch(() => {});
    // Load Elfsight Instagram Feed script
    if (!document.querySelector('script[src*="elfsightcdn.com"]')) {
      const s = document.createElement("script");
      s.src = "https://elfsightcdn.com/platform.js";
      s.async = true;
      document.body.appendChild(s);
    }
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: "Layanan", href: "#layanan" },
    { label: "Toko", href: "/toko", isRoute: true },
    { label: "Galeri", href: "/galeri", isRoute: true },
    { label: "Testimonial", href: "/testimonial", isRoute: true },
    { label: "Karir", href: "/karir", isRoute: true },
    { label: "Tentang", href: "#tentang" },
    { label: "Kontak", href: "#kontak" },
  ];

  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const getImgUrl = (url) => url && url.startsWith("fi-barbershop") ? `${API_BASE}/api/files/${url}` : url;

  return (
    <div className={`min-h-screen ${isDark ? "bg-[#050505] text-white" : "bg-[#FAFAFA] text-[#1A1A1A]"}`}>
      {/* Navbar */}
      <nav data-testid="navbar" className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? (isDark ? "bg-[#050505]/95 backdrop-blur-xl border-b border-[#262626]/50" : "bg-white/97 backdrop-blur-xl border-b border-gray-200") : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO} alt="FI Barbershop Palembang" className="w-10 h-10 rounded-full" />
            <span className={`font-heading text-lg font-bold uppercase tracking-wider hidden sm:block ${scrolled && !isDark ? "text-[#1A1A1A]" : "text-white"}`}>FI Barbershop</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(l => l.isRoute ? (
              <Link key={l.href} to={l.href} className={`text-sm font-semibold uppercase tracking-wider transition-colors ${scrolled && !isDark ? "text-gray-700 hover:text-[#E60000]" : "text-white hover:text-[#E60000]"}`}>{l.label}</Link>
            ) : (
              <a key={l.href} href={l.href} className={`text-sm font-semibold uppercase tracking-wider transition-colors ${scrolled && !isDark ? "text-gray-700 hover:text-[#E60000]" : "text-white hover:text-[#E60000]"}`}>{l.label}</a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className={`w-9 h-9 flex items-center justify-center transition-colors ${scrolled && !isDark ? "text-gray-500 hover:text-gray-800" : "text-white/70 hover:text-yellow-400"}`} data-testid="theme-toggle">
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link to="/login" data-testid="nav-login-btn">
              <Button className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider text-sm h-9 px-5 rounded-none">Masuk</Button>
            </Link>
            <button data-testid="mobile-menu-btn" className="md:hidden text-white" onClick={() => setMobileMenu(!mobileMenu)}>
              {mobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className={`md:hidden ${isDark ? "bg-[#0F0F0F] border-t border-[#262626]" : "bg-white border-t border-gray-200"} px-6 py-4 space-y-3`}>
            {navLinks.map(l => l.isRoute ? (
              <Link key={l.href} to={l.href} onClick={() => setMobileMenu(false)} className={`block text-sm font-semibold uppercase tracking-wider ${isDark ? "text-gray-300" : "text-gray-600"} hover:text-[#E60000]`}>{l.label}</Link>
            ) : (
              <a key={l.href} href={l.href} onClick={() => setMobileMenu(false)} className={`block text-sm font-semibold uppercase tracking-wider ${isDark ? "text-gray-300" : "text-gray-600"} hover:text-[#E60000]`}>{l.label}</a>
            ))}
          </div>
        )}
      </nav>

      {/* HERO */}
      <section id="hero" data-testid="hero-section" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG_HERO} alt="Barbershop Palembang - FI Barbershop Interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <img src={LOGO} alt="FI Barbershop Palembang" className="w-24 h-24 mx-auto mb-8 rounded-full border-2 border-[#E60000]/50" />
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E60000] mb-4">BARBERSHOP PALEMBANG</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white mb-4" data-testid="hero-title">
            FI BARBERSHOP
          </h1>
          <p className="font-heading text-2xl sm:text-3xl font-bold tracking-tight uppercase text-gray-300 mb-8">SEKIP</p>
          <p className="text-base sm:text-lg text-gray-400 mb-10 max-w-xl mx-auto leading-relaxed">
            FI Barbershop Palembang adalah destinasi premium untuk potong rambut pria & perawatan rambut terbaik di kota Palembang. Capster profesional, suasana nyaman, hasil maksimal.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#layanan" data-testid="hero-cta-services">
              <Button className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider text-base h-12 px-8 rounded-none w-full sm:w-auto">
                Lihat Layanan <ChevronRight size={18} className="ml-2" />
              </Button>
            </a>
            <Link to="/login" data-testid="hero-cta-register">
              <Button variant="outline" className="border-white text-white hover:border-[#E60000] hover:text-[#E60000] font-bold uppercase tracking-wider text-base h-12 px-8 rounded-none bg-transparent w-full sm:w-auto">
                Daftar Member
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar - Full Width */}
      <section className="bg-[#E60000] py-6" data-testid="stats-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { val: "7+", label: "Tahun Pengalaman" },
            { val: "10K+", label: "Pelanggan Puas" },
            { val: "15+", label: "Capster Profesional" },
            { val: "4.9", label: "Rating Google" },
          ].map((s, i) => (
            <div key={i}>
              <p className="font-heading text-3xl font-black text-white">{s.val}</p>
              <p className="text-sm text-white/80 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* KEUNGGULAN */}
      <section data-testid="keunggulan-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${bg1}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">MENGAPA FI BARBERSHOP</p>
            <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight ${textMain} mb-4`}>Barbershop Palembang Terbaik</h2>
            <p className={textSub}>FI Barbershop Palembang memberikan pengalaman grooming yang berbeda. Setiap detail kami perhatikan untuk kepuasan Anda.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Feature Card 1 */}
            <div className={`group relative overflow-hidden border ${bgCard} hover:border-[#E60000]/40 transition-all duration-500`}>
              <div className="h-52 overflow-hidden">
                <img src={IMG_CUTTING} alt="Capster profesional FI Barbershop Palembang sedang memotong rambut" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 bg-[#E60000]/10 flex items-center justify-center mb-3">
                  <Scissors size={20} className="text-[#E60000]" />
                </div>
                <h3 className={`font-heading text-xl font-bold uppercase ${textMain} mb-2`}>Capster Berpengalaman</h3>
                <p className={`${textSub} text-sm leading-relaxed`}>Tim capster profesional FI Barbershop Palembang terlatih dalam berbagai teknik potong rambut modern: fade, undercut, pompadour, dan gaya terkini lainnya.</p>
              </div>
            </div>
            {/* Feature Card 2 */}
            <div className={`group relative overflow-hidden border ${bgCard} hover:border-[#E60000]/40 transition-all duration-500`}>
              <div className="h-52 overflow-hidden">
                <img src={IMG_INTERIOR} alt="Interior barbershop modern FI Barbershop Palembang" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 bg-[#E60000]/10 flex items-center justify-center mb-3">
                  <Shield size={20} className="text-[#E60000]" />
                </div>
                <h3 className={`font-heading text-xl font-bold uppercase ${textMain} mb-2`}>Suasana Premium</h3>
                <p className={`${textSub} text-sm leading-relaxed`}>Interior modern dan nyaman menjadi ciri khas barbershop Palembang kami. Nikmati waktu grooming Anda dengan suasana yang rileks dan maskulin.</p>
              </div>
            </div>
            {/* Feature Card 3 */}
            <div className={`group relative overflow-hidden border ${bgCard} hover:border-[#E60000]/40 transition-all duration-500`}>
              <div className="h-52 overflow-hidden">
                <img src={IMG_TOOLS} alt="Peralatan barbershop premium - FI Barbershop Palembang" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 bg-[#E60000]/10 flex items-center justify-center mb-3">
                  <Zap size={20} className="text-[#E60000]" />
                </div>
                <h3 className={`font-heading text-xl font-bold uppercase ${textMain} mb-2`}>Alat & Produk Terbaik</h3>
                <p className={`${textSub} text-sm leading-relaxed`}>Kami hanya menggunakan peralatan berkualitas tinggi dan produk perawatan rambut terpercaya untuk hasil potongan yang presisi dan tahan lama.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LAYANAN */}
      <section id="layanan" data-testid="services-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${bg2}`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">LAYANAN KAMI</p>
              <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight ${textMain} mb-2`}>Layanan Premium</h2>
              <p className={`${textSub} max-w-lg`}>Berbagai layanan potong rambut, pewarnaan, treatment, dan styling terbaik di FI Barbershop Palembang.</p>
            </div>
            <Link to="/login" className="shrink-0">
              <Button variant="outline" className="border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white font-bold uppercase tracking-wider rounded-none h-10 text-xs">
                Booking Sekarang <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
          {services.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((s) => {
                const Icon = categoryIcons[s.category] || Scissors;
                return (
                  <div key={s.id} data-testid={`service-card-${s.id}`} className={`border ${bgCard} p-7 hover:border-[#E60000]/50 hover:-translate-y-1 transition-all duration-300 group`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 bg-[#E60000]/10 flex items-center justify-center group-hover:bg-[#E60000]/20 transition-colors">
                        <Icon size={18} className="text-[#E60000]" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600">{s.category}</span>
                    </div>
                    <h3 className={`font-heading text-xl font-bold uppercase tracking-tight ${textMain} mb-2`}>{s.name}</h3>
                    <p className={`${textMuted} text-sm mb-5 leading-relaxed line-clamp-2`}>{s.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-[#1A1A1A]">
                      <span className="font-heading text-2xl font-bold text-[#E60000]">{formatPrice(s.price)}</span>
                      <span className="text-xs text-gray-600 flex items-center gap-1"><Clock size={12} />{s.duration} mnt</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#0F0F0F] border border-[#1A1A1A]">
              <Scissors size={40} className="text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">Layanan sedang dimuat...</p>
            </div>
          )}
        </div>
      </section>

      {/* GALERI GAYA RAMBUT */}
      <section data-testid="style-gallery-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${isDark ? "bg-[#050505]" : "bg-[#E60000]"}`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className={`text-xs font-bold uppercase tracking-[0.2em] ${isDark ? "text-[#E60000]" : "text-white/70"} mb-3`}>INSPIRASI</p>
              <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-2`}>Galeri Gaya Rambut</h2>
              <p className={`${isDark ? "text-gray-400" : "text-white/80"} max-w-lg`}>Temukan inspirasi gaya rambut terbaru. Konsultasikan dengan capster FI Barbershop Palembang untuk mendapatkan gaya yang sesuai dengan wajah Anda.</p>
            </div>
            <Link to="/galeri" className="shrink-0">
              <Button variant="outline" className={`${isDark ? "border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white" : "border-white text-white hover:bg-white hover:text-[#E60000]"} font-bold uppercase tracking-wider rounded-none h-10 text-xs`}>
                <Camera size={14} className="mr-2" /> Lihat Semua Galeri
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {/* Show gallery from API if available, else show stock images. Exclude kids (separate section) */}
            {(() => {
              const nonKids = gallery.filter(g => g.category !== "kids");
              return nonKids.length > 0 ? nonKids.slice(0, 6).map((item, i) => (
                <button type="button" onClick={() => setLightbox({ src: getImgUrl(item.image_url), name: item.name, description: item.description, category: item.category })} key={item.id || i} className="relative group overflow-hidden aspect-[3/4] bg-[#0F0F0F] border border-[#1A1A1A] text-left" data-testid={`gallery-item-${item.id || i}`}>
                  <img src={getImgUrl(item.image_url)} alt={item.name || `Gaya rambut barbershop Palembang ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {item.name && <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300"><p className="font-heading text-sm font-bold uppercase text-white">{item.name}</p></div>}
                </button>
              )) : [
                { img: IMG_STYLE1, label: "Textured Crop" },
                { img: IMG_STYLE2, label: "Classic Fade" },
                { img: IMG_STYLE3, label: "Modern Pompadour" },
                { img: IMG_CUTTING, label: "Precision Cut" },
                { img: IMG_SALON, label: "Premium Experience" },
                { img: IMG_INTERIOR, label: "Our Space" },
              ].map((item, i) => (
                <button type="button" onClick={() => setLightbox({ src: item.img, name: item.label })} key={i} className="relative group overflow-hidden aspect-[3/4] bg-[#0F0F0F] border border-[#1A1A1A] text-left">
                  <img src={item.img} alt={`${item.label} - Barbershop Palembang FI Barbershop`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="font-heading text-sm font-bold uppercase text-white">{item.label}</p>
                  </div>
                </button>
              ));
            })()}
          </div>
          {gallery.filter(g => g.category !== "kids").length > 6 && (
            <div className="text-center mt-8">
              <Link to="/galeri">
                <Button variant="outline" className={`${isDark ? "border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white" : "border-white text-white hover:bg-white hover:text-[#E60000]"} font-bold uppercase tracking-wider rounded-none h-10 text-xs`} data-testid="gallery-see-more">
                  Lihat Selengkapnya ({gallery.filter(g => g.category !== "kids").length - 6} lainnya) <ChevronRight size={14} className="ml-1" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* KIDS STYLE SECTION (only if admin uploaded kids items) */}
      {gallery.filter(g => g.category === "kids").length > 0 && (
        <section data-testid="kids-style-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${isDark ? "bg-[#0A0A0A]" : "bg-white"}`}>
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">KHUSUS ANAK</p>
                <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight ${textMain} mb-2`}>Kids Style</h2>
                <p className={`${textSub} max-w-lg`}>Inspirasi gaya rambut khusus untuk anak-anak. Aman, nyaman, dan kekinian — dengan capster yang sabar dan berpengalaman menangani si kecil.</p>
              </div>
              <Link to="/galeri?cat=kids" className="shrink-0">
                <Button variant="outline" className={`${isDark ? "border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white" : "border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"} font-bold uppercase tracking-wider rounded-none h-10 text-xs`}>
                  <Camera size={14} className="mr-2" /> Lihat Semua Kids Style
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {gallery.filter(g => g.category === "kids").slice(0, 8).map((item, i) => (
                <button type="button" onClick={() => setLightbox({ src: getImgUrl(item.image_url), name: item.name, description: item.description, category: "kids" })} key={item.id || i} className="relative group overflow-hidden aspect-[3/4] bg-[#0F0F0F] border border-[#1A1A1A] text-left" data-testid={`kids-item-${item.id || i}`}>
                  <img src={getImgUrl(item.image_url)} alt={item.name || `Kids hair style ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {item.name && <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300"><p className="font-heading text-xs font-bold uppercase text-white">{item.name}</p></div>}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TIM CAPSTER */}
      {capsters.length > 0 && (
        <section data-testid="capster-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${bg2}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">TIM KAMI</p>
              <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight ${textMain} mb-4`}>Capster Profesional</h2>
              <p className={`${textSub} max-w-2xl mx-auto`}>Kenalan dengan tim capster berpengalaman FI Barbershop Palembang yang siap memberikan hasil terbaik untuk penampilanmu.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-5">
              {capsters.slice(0, 6).map(c => (
                <div key={c.id} className="group text-center w-full sm:w-[calc(50%-0.625rem)] md:w-[calc(33.333%-0.834rem)] lg:w-[calc(25%-0.9375rem)] max-w-[280px]" data-testid={`capster-card-${c.id}`}>
                  <div className="relative w-full aspect-[3/4] mb-4 overflow-hidden border border-[#1A1A1A] bg-[#0F0F0F]">
                    {c.photo_url ? (
                      <img src={c.photo_url} alt={`${c.name} - Capster FI Barbershop Palembang`} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: "center top" }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#0F0F0F]">
                        <Scissors size={40} className="text-gray-700" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/80 to-transparent" />
                  </div>
                  <h3 className={`font-heading text-base font-bold uppercase ${textMain}`}>{c.name}</h3>
                  <p className="text-xs text-[#E60000] font-semibold uppercase tracking-wider">Capster</p>
                </div>
              ))}
            </div>
            {capsters.length > 6 && (
              <div className="text-center mt-8">
                <Link to="/galeri">
                  <Button variant="outline" className="border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white font-bold uppercase tracking-wider rounded-none h-10 text-xs" data-testid="capster-see-more">
                    Lihat Selengkapnya ({capsters.length - 6} lainnya) <ChevronRight size={14} className="ml-1" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* GOOGLE REVIEWS */}
      {googleReviews.length > 0 && (
        <section data-testid="reviews-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${bg1}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">TESTIMONI</p>
              <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight ${textMain} mb-4`}>Apa Kata Pelanggan</h2>
              <p className={`${textSub} max-w-xl mx-auto`}>Review bintang 5 langsung dari Google Maps. Terima kasih atas kepercayaan pelanggan FI Barbershop Palembang!</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {googleReviews.slice(0, 6).map((r, i) => (
                <div key={i} className={`border ${bgCard} p-6 hover:border-[#E60000]/30 transition-colors`} data-testid={`review-card-${i}`}>
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map(s => (
                      <svg key={s} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                    ))}
                  </div>
                  <p className={`${textSub} text-sm leading-relaxed mb-5 line-clamp-4`}>&ldquo;{r.snippet}&rdquo;</p>
                  <div className={`flex items-center gap-3 pt-4 border-t ${borderC}`}>
                    {r.thumbnail ? (
                      <img src={r.thumbnail} alt={r.name} className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#E60000]/20 flex items-center justify-center text-[#E60000] font-bold text-sm">{r.name?.charAt(0)}</div>
                    )}
                    <div>
                      <p className={`${textMain} text-sm font-semibold`}>{r.name}</p>
                      <p className={`${textMuted} text-xs`}>{r.date} &middot; Google Reviews</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="https://search.google.com/local/writereview?placeid=ChIJ58zpU4B2Oy4RHCbmRBKUaak" target="_blank" rel="noopener noreferrer" data-testid="write-google-review-landing">
                <Button className={`${isDark ? "bg-white/5 hover:bg-white/10 border-[#262626] text-white" : "bg-[#E60000]/5 hover:bg-[#E60000]/10 border-[#E60000]/30 text-[#E60000]"} border font-bold uppercase rounded-none h-10 px-6`}>
                  <Star size={16} className="mr-2 text-yellow-400" /> Tulis Review Google
                </Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* INSTAGRAM FEED */}
      <section data-testid="instagram-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${bg2}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">INSTAGRAM</p>
            <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight ${textMain} mb-4`}>Follow Kami di Instagram</h2>
            <p className={`${textSub} max-w-xl mx-auto mb-6`}>Lihat update terbaru, behind the scene, dan inspirasi gaya rambut dari FI Barbershop Palembang.</p>
            <a href="https://www.instagram.com/fibarbershop.sekip" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#E60000] hover:text-[#FF1A1A] font-bold text-sm uppercase tracking-wider transition-colors" data-testid="ig-profile-link">
              @fibarbershop.sekip
            </a>
          </div>
          <div className="elfsight-app-acac3221-a9f9-4a28-9d5b-274e9455c21d" data-elfsight-app-lazy></div>
          <div className="text-center mt-8">
            <a href="https://www.instagram.com/fibarbershop.sekip" target="_blank" rel="noopener noreferrer" data-testid="ig-follow-btn">
              <Button className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] hover:opacity-90 text-white font-bold uppercase rounded-none h-10 px-8">
                Follow @fibarbershop.sekip
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* TENTANG */}
      <section id="tentang" data-testid="about-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${bg2}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">TENTANG KAMI</p>
              <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight ${textMain} mb-6 leading-[0.95]`}>Lebih Dari Sekedar Barbershop Palembang Biasa</h2>
              <p className={`${textSub} leading-relaxed mb-4`}>
                <strong className={isDark ? "text-gray-200" : "text-[#1A1A1A]"}>FI Barbershop Palembang</strong> hadir di Jl. Ampibi, Kec. Kemuning dengan konsep premium barbershop yang mengutamakan kualitas pelayanan dan kenyamanan. Berdiri sejak 2018, kami berkomitmen memberikan pengalaman grooming terbaik untuk setiap pelanggan di kota Palembang.
              </p>
              <p className={`${textSub} leading-relaxed mb-8`}>
                Sebagai barbershop Palembang yang terpercaya, kami menyediakan layanan potong rambut, pewarnaan, treatment rambut, perawatan jenggot, dan styling dengan teknik terkini. Setiap capster kami telah melalui pelatihan intensif untuk memastikan hasil yang konsisten dan memuaskan.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Scissors, title: "Teknik Modern", desc: "Fade, undercut, pompadour & lainnya" },
                  { icon: Star, title: "Produk Premium", desc: "Brand internasional pilihan" },
                  { icon: Users, title: "Loyalitas Member", desc: "5x potong = 1x GRATIS" },
                  { icon: Shield, title: "Higienis & Steril", desc: "Alat disterilkan setiap pemakaian" },
                ].map((item, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 border ${bgCard}`}>
                    <div className={`w-8 h-8 ${isDark ? "bg-[#E60000]/10" : "bg-[#FFF0F0]"} flex items-center justify-center shrink-0 mt-0.5`}><item.icon size={14} className="text-[#E60000]" /></div>
                    <div>
                      <p className={`${textMain} text-sm font-bold`}>{item.title}</p>
                      <p className={`${textMuted} text-xs`}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <a href="#kontak">
                <Button className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-11 px-8">
                  Kunjungi Kami <ArrowRight size={16} className="ml-2" />
                </Button>
              </a>
            </div>
            <div className="order-1 lg:order-2 relative">
              <div className="grid grid-cols-2 gap-3">
                <img src={IMG_SALON} alt="FI Barbershop Palembang - suasana salon" className="w-full h-64 object-cover border border-[#1A1A1A] col-span-2" />
                <img src={IMG_CUTTING} alt="Potong rambut di FI Barbershop Palembang" className="w-full h-48 object-cover border border-[#1A1A1A]" />
                <img src={IMG_TOOLS} alt="Peralatan barbershop premium FI Barbershop" className="w-full h-48 object-cover border border-[#1A1A1A]" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#E60000] px-5 py-3 z-10">
                <p className="font-heading text-sm font-bold uppercase tracking-wider text-white">Sejak 2018</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOYALTY + TOKO */}
      <section className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${bg1}`}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Loyalty (text-based content) */}
          <div className={`border ${bgCard} p-8 md:p-10 flex flex-col justify-between`} data-testid="loyalty-section">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">PROGRAM LOYALITAS</p>
              <h2 className={`font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight ${textMain} mb-2`}>
                1x Daftar <span className="text-[#E60000]">Nomor HP</span>
              </h2>
              <p className={`font-heading text-base md:text-lg font-bold uppercase tracking-wider ${textSub} mb-5`}>
                Dapatkan Benefit <span className="text-[#E60000]">Eksklusif Selamanya!</span>
              </p>
              {/* Benefit grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {[
                  { icon: Crown, title: "FISEKIP PREMIUM", subtitle: "Selamanya Member", desc: "Nikmati semua benefit eksklusif tanpa biaya tambahan." },
                  { icon: Infinity, title: "Tanpa Batas Waktu", subtitle: "Selamanya Akses", desc: "Akses semua layanan kapan saja, selamanya." },
                  { icon: CalendarCheck, title: "Check-in Online", subtitle: "Datang Rutin", desc: "Lebih mudah, lebih praktis, dapat poin setiap kunjungan!" },
                ].map((b, i) => (
                  <div key={i} className={`${bgInner} border p-3 text-center`} data-testid={`loyalty-benefit-${i}`}>
                    <b.icon size={22} className="text-[#E60000] mx-auto mb-2" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#E60000] mb-1">{b.subtitle}</p>
                    <p className={`text-xs font-bold ${textMain} mb-1`}>{b.title}</p>
                    <p className={`text-[10px] ${textMuted} leading-snug`}>{b.desc}</p>
                  </div>
                ))}
              </div>
              {/* 5x flow */}
              <div className={`${bgInner} border p-4`} data-testid="loyalty-flow">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="bg-[#E60000] px-3 py-2 text-center shrink-0">
                    <p className="font-heading text-[10px] font-bold uppercase tracking-wider text-white">Gratis</p>
                    <p className="font-heading text-xl font-black text-white leading-none">1x</p>
                  </div>
                  <div className="flex-1 text-center">
                    <p className={`font-heading text-sm md:text-base font-bold uppercase tracking-tight ${textMain}`}>Potong Rambut</p>
                    <p className={`text-[11px] ${textSub} mt-0.5`}>Setelah <span className="text-[#E60000] font-bold">5x Pemotongan</span></p>
                  </div>
                  <div className="bg-[#E60000] px-3 py-2 text-center shrink-0">
                    <p className="font-heading text-[10px] font-bold uppercase tracking-wider text-white">Gratis</p>
                    <p className="font-heading text-xl font-black text-white leading-none">1x</p>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {[1,2,3,4,5].map(n => (
                    <div key={n} className={`aspect-square flex items-center justify-center border font-heading text-sm font-bold ${n === 5 ? "border-[#E60000] bg-[#E60000] text-white" : `border-[#262626] ${isDark ? "bg-[#0A0A0A] text-gray-500" : "bg-gray-50 text-gray-400"}`}`}>
                      {n}
                    </div>
                  ))}
                </div>
                <p className={`text-center text-[10px] font-bold uppercase tracking-wider ${textSub} mt-3`}>Lebih sering datang, lebih banyak benefit!</p>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/login" data-testid="loyalty-cta">
                <Button className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-11 rounded-none">
                  <Gift size={16} className="mr-2" /> Daftar Member Gratis
                </Button>
              </Link>
            </div>
          </div>

          {/* Toko Online (real products) */}
          <div className={`border ${bgCard} p-8 md:p-10 flex flex-col justify-between`} data-testid="toko-section">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">TOKO ONLINE</p>
              <h2 className={`font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight ${textMain} mb-4`}>Produk Perawatan<br />Rambut Terbaik</h2>
              <p className={`${textSub} text-sm leading-relaxed mb-6`}>
                Dapatkan produk perawatan rambut, pomade, hair wax, shampoo anti-ketombe, dan beard oil premium langsung dari toko online FI Barbershop Palembang. Pengiriman ke seluruh Indonesia!
              </p>
            </div>
            <div>
              {products.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 mb-6">
                  {products.slice(0, 3).map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => setProductDetail(p)}
                      className={`${bgInner} border p-3 hover:border-[#E60000] transition-colors group text-left`}
                      data-testid={`toko-product-${p.id}`}
                    >
                      <div className="aspect-square bg-[#0A0A0A] mb-2 overflow-hidden flex items-center justify-center">
                        {p.image_url ? (
                          <img src={getImgUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <ShoppingBag size={24} className="text-gray-600" />
                        )}
                      </div>
                      <p className={`text-[11px] ${textMain} font-semibold truncate`}>{p.name}</p>
                      <p className="text-[10px] text-[#E60000] font-bold mt-0.5">{formatPrice(p.price)}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 mb-6">
                  {[
                    { name: "Pomade", icon: "P" },
                    { name: "Hair Wax", icon: "W" },
                    { name: "Shampoo", icon: "S" },
                  ].map((p, i) => (
                    <div key={i} className={`${bgInner} border p-4 text-center`}>
                      <div className="w-10 h-10 bg-[#E60000]/10 flex items-center justify-center mx-auto mb-2 font-heading text-lg font-bold text-[#E60000]">{p.icon}</div>
                      <p className={`text-xs ${textSub} font-semibold`}>{p.name}</p>
                    </div>
                  ))}
                </div>
              )}
              <Link to="/toko" data-testid="toko-cta">
                <Button className={`w-full ${isDark ? "bg-white/5 hover:bg-white/10 border-[#262626] text-white" : "bg-[#E60000]/5 hover:bg-[#E60000]/10 border-[#E60000]/30 text-[#E60000]"} border font-bold uppercase tracking-wider h-11 rounded-none`}>
                  <ShoppingBag size={16} className="mr-2" /> Kunjungi Toko {products.length > 0 ? `(${products.length} produk)` : ""}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG_SALON} alt="FI Barbershop Palembang" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#E60000]/85" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tight text-white mb-4">Siap Tampil Beda?</h2>
          <p className="text-white/90 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            Kunjungi FI Barbershop Palembang sekarang dan rasakan pengalaman potong rambut premium. Capster profesional kami siap membuat Anda tampil lebih percaya diri!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#kontak">
              <Button className="bg-white text-[#E60000] hover:bg-gray-100 font-bold uppercase tracking-wider h-12 px-8 rounded-none">
                <MapPin size={16} className="mr-2" /> Lokasi Kami
              </Button>
            </a>
            <Link to="/login">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#E60000] font-bold uppercase tracking-wider h-12 px-8 rounded-none">
                Daftar Sekarang
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* KONTAK */}
      <section id="kontak" data-testid="contact-section" className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${bg2}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-3">KONTAK</p>
            <h2 className={`font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight ${textMain} mb-4`}>Kunjungi FI Barbershop Palembang</h2>
            <p className={`${textSub} max-w-xl mx-auto`}>Kami berlokasi strategis di Jl. Ampibi, Kec. Kemuning, Palembang. Mudah diakses dan tersedia parkir luas.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className={`border ${bgCard} p-6 text-center hover:border-[#E60000]/40 transition-colors`}>
              <div className="w-12 h-12 bg-[#E60000]/10 flex items-center justify-center mx-auto mb-4"><MapPin size={22} className="text-[#E60000]" /></div>
              <h3 className={`font-heading text-base font-bold uppercase ${textMain} mb-2`}>Alamat</h3>
              <p className={`${textSub} text-sm leading-relaxed`}>Jl. Ampibi, 20 Ilir D II, Kec. Kemuning, Kota Palembang, Sumatera Selatan 30127</p>
            </div>
            <div className={`border ${bgCard} p-6 text-center hover:border-[#E60000]/40 transition-colors`}>
              <div className="w-12 h-12 bg-[#E60000]/10 flex items-center justify-center mx-auto mb-4"><Clock size={22} className="text-[#E60000]" /></div>
              <h3 className={`font-heading text-base font-bold uppercase ${textMain} mb-2`}>Jam Buka</h3>
              <p className={`${textSub} text-sm`}>Setiap Hari<br /><span className={`${textMain} font-semibold`}>10:00 AM - 9:00 PM</span></p>
            </div>
            <div className={`border ${bgCard} p-6 text-center hover:border-[#E60000]/40 transition-colors`}>
              <div className="w-12 h-12 bg-[#E60000]/10 flex items-center justify-center mx-auto mb-4"><Instagram size={22} className="text-[#E60000]" /></div>
              <h3 className={`font-heading text-base font-bold uppercase ${textMain} mb-2`}>Instagram</h3>
              <a href="https://www.instagram.com/fibarbershop.sekip" target="_blank" rel="noopener noreferrer" className={`${textSub} text-sm hover:text-[#E60000] transition-colors`} data-testid="instagram-link">@fibarbershop.sekip</a>
            </div>
            <div className={`border ${bgCard} p-6 text-center hover:border-[#E60000]/40 transition-colors`}>
              <div className="w-12 h-12 bg-[#E60000]/10 flex items-center justify-center mx-auto mb-4"><Facebook size={22} className="text-[#E60000]" /></div>
              <h3 className={`font-heading text-base font-bold uppercase ${textMain} mb-2`}>Facebook</h3>
              <a href="https://www.facebook.com/BarbershopPalembang" target="_blank" rel="noopener noreferrer" className={`${textSub} text-sm hover:text-[#E60000] transition-colors`} data-testid="facebook-link">BarbershopPalembang</a>
            </div>
          </div>
          {/* Map Embed */}
          <div className="mt-8 border border-[#1A1A1A] overflow-hidden">
            <iframe
              title="Lokasi FI Barbershop Palembang"
              src="https://maps.google.com/maps?q=FI+Barbershop+Sekip,+Jl.+Ampibi,+20+Ilir+D+II,+Kec.+Kemuning,+Palembang&t=&z=17&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="300"
              style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) grayscale(20%)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer data-testid="footer" className={`border-t ${isDark ? "border-[#1A1A1A] bg-[#050505]" : "border-[#E60000] bg-[#E60000]"} text-white`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO} alt="FI Barbershop Palembang" className="w-10 h-10 rounded-full" />
                <div>
                  <p className="font-heading text-lg font-bold uppercase text-white">FI Barbershop Sekip</p>
                  <p className={`text-xs ${isDark ? "text-gray-500" : "text-white/70"}`}>Fashion International Barbershop</p>
                </div>
              </div>
              <p className={`${isDark ? "text-gray-500" : "text-white/80"} text-sm leading-relaxed max-w-sm mb-4`}>
                FI Barbershop Palembang adalah barbershop premium di Jl. Ampibi, Kec. Kemuning yang menyediakan layanan potong rambut, perawatan, dan grooming pria terbaik di kota Palembang. Berdiri sejak 2018.
              </p>
              <div className="flex items-center gap-4">
                <a href="https://www.instagram.com/fibarbershop.sekip" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 ${isDark ? "bg-[#1A1A1A] border-[#262626] text-gray-400 hover:border-[#E60000] hover:text-[#E60000]" : "bg-white/20 border-white/30 text-white hover:bg-white/30"} border flex items-center justify-center transition-colors`} data-testid="footer-instagram"><Instagram size={16} /></a>
                <a href="https://www.facebook.com/BarbershopPalembang" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 ${isDark ? "bg-[#1A1A1A] border-[#262626] text-gray-400 hover:border-[#E60000] hover:text-[#E60000]" : "bg-white/20 border-white/30 text-white hover:bg-white/30"} border flex items-center justify-center transition-colors`} data-testid="footer-facebook"><Facebook size={16} /></a>
              </div>
            </div>
            <div>
              <p className={`font-heading text-xs font-bold uppercase tracking-widest ${isDark ? "text-gray-400" : "text-white/70"} mb-4`}>Navigasi</p>
              <div className="space-y-2">
                <a href="#layanan" className={`block text-sm ${isDark ? "text-gray-500 hover:text-[#E60000]" : "text-white/80 hover:text-white"} transition-colors`}>Layanan</a>
                <Link to="/toko" className={`block text-sm ${isDark ? "text-gray-500 hover:text-[#E60000]" : "text-white/80 hover:text-white"} transition-colors`}>Toko Online</Link>
                <Link to="/galeri" className={`block text-sm ${isDark ? "text-gray-500 hover:text-[#E60000]" : "text-white/80 hover:text-white"} transition-colors`}>Galeri</Link>
                <Link to="/karir" className={`block text-sm ${isDark ? "text-gray-500 hover:text-[#E60000]" : "text-white/80 hover:text-white"} transition-colors`}>Karir</Link>
              </div>
            </div>
            <div>
              <p className={`font-heading text-xs font-bold uppercase tracking-widest ${isDark ? "text-gray-400" : "text-white/70"} mb-4`}>Info</p>
              <div className="space-y-2">
                <p className={`text-sm ${isDark ? "text-gray-500" : "text-white/80"}`}>Jl. Ampibi, 20 Ilir D II</p>
                <p className={`text-sm ${isDark ? "text-gray-500" : "text-white/80"}`}>Kec. Kemuning, Palembang 30127</p>
                <p className={`text-sm ${isDark ? "text-gray-500" : "text-white/80"}`}>Buka: 10:00 AM - 9:00 PM</p>
                <p className={`text-sm ${isDark ? "text-gray-500" : "text-white/80"}`}>Setiap Hari</p>
              </div>
            </div>
          </div>
          <div className={`border-t ${isDark ? "border-[#1A1A1A]" : "border-white/20"} pt-6 flex flex-col md:flex-row items-center justify-between gap-4`}>
            <p className={`text-xs ${isDark ? "text-gray-600" : "text-white/60"}`}>&copy; {new Date().getFullYear()} FI Barbershop Sekip Palembang. All rights reserved.</p>
            <p className={`text-xs ${isDark ? "text-gray-700" : "text-white/50"}`}>Barbershop Palembang | FI Barbershop Palembang | Potong Rambut Palembang</p>
          </div>
        </div>
      </footer>

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={productDetail}
        onClose={() => setProductDetail(null)}
        onBuyNow={(p) => { setProductDetail(null); navigate(`/toko?buy=${p.id}`); }}
        showAddCart={false}
        buyLabel="Beli Sekarang"
      />

      {/* Image Lightbox (Gallery / Kids Style) */}
      <Dialog open={!!lightbox} onOpenChange={(o) => { if (!o) setLightbox(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-3xl rounded-none p-0 overflow-hidden" data-testid="image-lightbox">
          {lightbox && (
            <div>
              <div className="bg-[#050505] flex items-center justify-center" style={{ maxHeight: "70vh" }}>
                <img src={lightbox.src} alt={lightbox.name || "Detail"} className="w-full h-auto object-contain" style={{ maxHeight: "70vh" }} data-testid="lightbox-image" />
              </div>
              <div className="p-5 border-t border-[#262626]">
                {lightbox.category && (
                  <span className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-[#E60000] mb-2">
                    {lightbox.category === "kids" ? "Kids Style" : lightbox.category === "warna" ? "Hair Color" : "Model Potong"}
                  </span>
                )}
                {lightbox.name && <h3 className="font-heading text-xl font-bold uppercase tracking-tight mb-1" data-testid="lightbox-name">{lightbox.name}</h3>}
                {lightbox.description && <p className="text-sm text-gray-400 leading-relaxed">{lightbox.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
